import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  LinearProgress,
  Paper,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { syllabusToUnits, unitsToSyllabusPayload } from '../utils/curriculumMappers.ts';
import type { Unit, Topic, Week, Subtopic, Day , UnitWiseSyllabus } from '../types/curriculum.types.ts';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { useRecoilState, useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import BoardClassSubjectSelector from './Boardclasssubjectselector.tsx';
import { curriculumSelectionAtom, knowledgeMapsAtom } from './Curriculumselectionatom.ts';
import TabularDayPlanner from './TabularDayPlanner.tsx';
import UnitItem from './syllabus/UnitWiseSyllabus/Unititem.tsx';


// ─── Helpers ──────────────────────────────────────────────────────────────────

let _id = 1;
const uid = () => `id_${_id++}_${Date.now()}`;

function flattenPrerequisites(prerequisites: any[]): string[] {
  if (!prerequisites?.length) return [];
  const result: string[] = [];
  const traverse = (node: any) => {
    if (node.name) result.push(node.name);
    node.children?.forEach(traverse);
  };
  prerequisites.forEach(traverse);
  return result;
}

function createDefaultDays(weekNumber: number, subtopics: Subtopic[] = []): Day[] {
  const weekdays: Day['dayOfWeek'][] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const subtopicNames = subtopics.map(st => st.name);

  return weekdays.map((day, index) => {
    const isSunday = day === 'Sunday';
    let assignedSubtopic = '';
    let assignedSubtopicId = '';
    if (!isSunday && subtopicNames.length > 0) {
      const subtopicIndex = index % subtopicNames.length;
      assignedSubtopic = subtopicNames[subtopicIndex] || '';
      assignedSubtopicId = subtopics[subtopicIndex]?.id || '';
    }
    return {
      id: uid(),
      dayOfWeek: day,
      objectives: assignedSubtopic ? [assignedSubtopic] : [],
      activities: [],
      materials: [],
      homework: '',
      isHoliday: isSunday,
      holidayType: isSunday ? 'weekly_off' : undefined,
      holidayReason: isSunday ? 'Weekly Holiday' : '',
      isReviewDay: isSunday || (weekNumber % 2 === 0 && day === 'Saturday'),
      notes: '',
      customTitle: isSunday ? 'Holiday - Weekly Off' : '',
      subtopicId: assignedSubtopicId || undefined,
      topicId: undefined,
    } as Day;
  });
}

function enrichTopics(rawTopics: any[]): Topic[] {
  return (rawTopics ?? []).map((t: any) => ({
    id: t.id || uid(),
    name: t.topic ?? '',
    description: t.description || '',
    duration: t.duration || (t.weeks?.length || 1),
    marks: t.marks || 0,
    weeks: [],
    prerequisites: t.topic_prerequisites || [],
    learning_material: t.learning_material || [],
    subtopics: (t.subtopics || []).map((st: any) => ({
      id: st.id || uid(),
      name: st.name || st.subtopic || 'Subtopic',
      marks: st.marks || 0,
      prerequisites: st.prerequisites || [],
    })),
  }));
}

function buildDefaultUnits(topics: Topic[]): Unit[] {
  const unit: Unit = {
    id: uid(),
    name: 'Unit 1',
    topics,
    totalWeeks: topics.reduce((sum, t) => sum + t.duration, 0),
  };
  unit.topics.forEach(topic => {
    topic.weeks = Array.from({ length: topic.duration || 1 }, (_, i) => ({
      id: uid(),
      name: `Week ${i + 1}`,
      weekNumber: i + 1,
      topicId: topic.id,
      subtopics: topic.subtopics || [],
      days: createDefaultDays(i + 1, topic.subtopics || []),
      totalMarks: (topic.subtopics || []).reduce((sum, st) => sum + (st.marks || 0), 0),
      notes: '',
    }));
  });
  return [unit];
}

function restoreOrBuildUnits(topics: Topic[], apiUnits: Unit[] | null): Unit[] {
  if (apiUnits && apiUnits.length > 0) {
    return apiUnits.map((savedUnit) => ({
      id: savedUnit.id || uid(),
      name: savedUnit.name,
      topics: savedUnit.topics.map((savedTopic) => {
        const originalTopic = topics.find((t) => t.name === savedTopic.name);
        return {
          id: savedTopic.id || uid(),
          name: savedTopic.name,
          description: savedTopic.description || originalTopic?.description,
          duration: savedTopic.duration || originalTopic?.duration || savedTopic.weeks.length,
          marks: savedTopic.marks || originalTopic?.marks || 0,
          weeks: savedTopic.weeks.map((w, index) => ({
            id: w.id || uid(),
            name: w.name || `Week ${index + 1}`,
            weekNumber: w.weekNumber || index + 1,
            topicId: w.topicId || savedTopic.id,
            subtopics: w.subtopics.map((st) => ({
              id: st.id || uid(),
              name: st.name,
              marks: st.marks || 0,
              prerequisites: st.prerequisites || [],
            })),
            days: w.days && w.days.length > 0
              ? w.days.map((d) => ({
                  id: d.id || uid(),
                  dayOfWeek: d.dayOfWeek,
                  objectives: d.objectives || [],
                  activities: d.activities || [],
                  materials: d.materials || [],
                  homework: d.homework || '',
                  isHoliday: d.isHoliday || false,
                  holidayType: d.holidayType || undefined,
                  holidayReason: d.holidayReason || '',
                  isReviewDay: d.isReviewDay || false,
                  notes: d.notes || '',
                  customTitle: d.customTitle || '',
                  subtopicId: d.subtopicId || undefined,
                  topicId: d.topicId || w.topicId || savedTopic.id,
                }))
              : createDefaultDays(index + 1, w.subtopics),
            totalMarks: w.subtopics.reduce((sum, st) => sum + (st.marks || 0), 0),
            notes: w.notes || '',
          })),
          prerequisites: originalTopic?.prerequisites || [],
          learning_material: originalTopic?.learning_material || [],
          subtopics: originalTopic?.subtopics || [],
        };
      }),
      totalWeeks: savedUnit.topics.reduce((sum, t) => sum + (t.duration || t.weeks.length), 0),
    }));
  }
  return buildDefaultUnits(topics);
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface KnowledgeMapManagerProps {
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
  onNavigateToCurriculum?: (board: string, classVal: string, subject: string) => void;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function KnowledgeMapManager({ initialBoard, initialClass, initialSubject, onNavigateToCurriculum }: KnowledgeMapManagerProps) {
  const { showSnackBar } = useSnackBar();
  const aiActions = AiActions();
  const [curriculumSelection, setCurriculumSelection] = useRecoilState(curriculumSelectionAtom);
  const { board: selectedBoard, classVal: selectedClass, subject: selectedSubject } = curriculumSelection;
  const knowledgeMaps = useRecoilValue(knowledgeMapsAtom);

  const [currentKnowledgeMap, setCurrentKnowledgeMap] = useState<any>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [syllabusId, setSyllabusId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTabularView, setShowTabularView] = useState(false);

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Unit Wise Syllabus', href: '/knowledge-map' },
    ...(selectedBoard ? [{ label: selectedBoard }] : []),
    ...(selectedClass ? [{ label: `Class ${selectedClass}` }] : []),
    ...(selectedSubject ? [{ label: selectedSubject }] : []),
  ];

  // ── Apply initial values ────────────────────────────────────────────────────
  useEffect(() => {
    if (knowledgeMaps.length > 0 && initialBoard && initialClass && initialSubject) {
      const mapExists = knowledgeMaps.some((km: any) =>
        km.board === initialBoard && km.class === initialClass && km.subject === initialSubject
      );
      if (mapExists) {
        setCurriculumSelection({ board: initialBoard, classVal: initialClass, subject: initialSubject });
      }
    }
  }, [knowledgeMaps, initialBoard, initialClass, initialSubject]);

  // ── Load units from API ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedBoard || !selectedClass || !selectedSubject) {
      setCurrentKnowledgeMap(null);
      setUnits([]);
      setSyllabusId(null);
      return;
    }

    const found = knowledgeMaps.find(
      (km: any) => km.board === selectedBoard && km.class === selectedClass && km.subject === selectedSubject
    );

    setCurrentKnowledgeMap(found ?? null);

    if (!found?.topics) {
      setUnits([]);
      setSyllabusId(null);
      return;
    }

    const loadUnits = async () => {
      try {
        setLoading(true);
        const enriched = enrichTopics(found.topics);
        const syllabusList = await aiActions.getSyllabusList(found.id ?? found._id);

        if (syllabusList?.length > 0) {
          const syllabus = syllabusList[0] as UnitWiseSyllabus;
          setSyllabusId(syllabus._id);

          if (syllabus.terms && syllabus.terms.length > 0) {
            const allUnits: Unit[] = [];
            syllabus.terms.forEach((term: any) => {
              term.units?.forEach((termUnit: any) => {
                const matchingTopic = enriched.find((t: Topic) => t.name === termUnit.name);
                if (matchingTopic) {
                  allUnits.push({ id: termUnit.id || uid(), name: termUnit.name, topics: [matchingTopic], totalWeeks: matchingTopic.duration });
                }
              });
            });
            if (allUnits.length > 0) {
              setUnits(allUnits);
              setHasChanges(false);
              setLoading(false);
              return;
            }
          }
          setUnits(restoreOrBuildUnits(enriched, syllabusToUnits(syllabus)));
        } else {
          setSyllabusId(null);
          setUnits(restoreOrBuildUnits(enriched, null));
        }
        setHasChanges(false);
      } catch (err) {
        console.error(err);
        setUnits(restoreOrBuildUnits(enrichTopics(found.topics), null));
      } finally {
        setLoading(false);
      }
    };

    loadUnits();
  }, [selectedBoard, selectedClass, selectedSubject, knowledgeMaps]);

  const markChanged = () => setHasChanges(true);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalTopics   = units.reduce((s, u) => s + u.topics.length, 0);
  const totalWeeks    = units.reduce((s, u) => s + u.topics.reduce((a, t) => a + t.duration, 0), 0);
  const totalDays     = units.reduce((s, u) => s + u.topics.reduce((a, t) => a + t.weeks.reduce((b, w) => b + w.days.length, 0), 0), 0);
  const totalHolidays = units.reduce((s, u) => s + u.topics.reduce((a, t) => a + t.weeks.reduce((b, w) => b + w.days.filter((d: Day) => d.isHoliday).length, 0), 0), 0);
  const totalMarks    = units.reduce((s, u) => s + u.topics.reduce((a, t) => a + t.weeks.reduce((b, w) => b + w.subtopics.reduce((c, st) => c + (st.marks || 0), 0), 0), 0), 0);

  // ── CRUD Helpers ─────────────────────────────────────────────────────────────

  const updateUnits = (updater: (prev: Unit[]) => Unit[]) => { setUnits(updater); markChanged(); };

  const mapUnit   = (id: string, fn: (u: Unit) => Unit) => (prev: Unit[]) => prev.map(u => u.id === id ? fn(u) : u);
  const mapTopic  = (uid: string, tid: string, fn: (t: Topic) => Topic) => mapUnit(uid, u => ({ ...u, topics: u.topics.map(t => t.id === tid ? fn(t) : t) }));
  const mapWeek   = (uid: string, tid: string, wid: string, fn: (w: Week) => Week) => mapTopic(uid, tid, t => ({ ...t, weeks: t.weeks.map(w => w.id === wid ? fn(w) : w) }));

  // ── Unit ops ──────────────────────────────────────────────────────────────
  const addUnit    = () => updateUnits(prev => [...prev, { id: uid(), name: `Unit ${prev.length + 1}`, topics: [], totalWeeks: 0 }]);
  const renameUnit = (id: string, name: string) => updateUnits(mapUnit(id, u => ({ ...u, name })));
  const deleteUnit = (id: string) => updateUnits(prev => {
    const target = prev.find(u => u.id === id);
    if (!target) return prev;
    const rest = prev.filter(u => u.id !== id);
    if (rest.length === 0) return prev;
    if (target.topics.length > 0) rest[0] = { ...rest[0], topics: [...rest[0].topics, ...target.topics] };
    return rest;
  });

  // ── Topic ops ─────────────────────────────────────────────────────────────
  const addTopic    = (unitId: string) => updateUnits(mapUnit(unitId, u => ({ ...u, topics: [...u.topics, { id: uid(), name: 'New Topic', description: '', duration: 1, marks: 0, weeks: [], prerequisites: [], learning_material: [], subtopics: [] }] })));
  const updateTopic = (unitId: string, topicId: string, patch: Partial<Topic>) => updateUnits(mapTopic(unitId, topicId, t => ({ ...t, ...patch })));
  const deleteTopic = (unitId: string, topicId: string) => updateUnits(mapUnit(unitId, u => ({ ...u, topics: u.topics.filter(t => t.id !== topicId) })));

  // ── Week ops ──────────────────────────────────────────────────────────────
  const addWeek    = (unitId: string, topicId: string) => updateUnits(mapTopic(unitId, topicId, t => ({ ...t, weeks: [...t.weeks, { id: uid(), name: `Week ${t.weeks.length + 1}`, weekNumber: t.weeks.length + 1, topicId, subtopics: [], days: createDefaultDays(t.weeks.length + 1, []), totalMarks: 0, notes: '' }] })));
  const updateWeek = (unitId: string, topicId: string, weekId: string, patch: Partial<Week>) => updateUnits(mapWeek(unitId, topicId, weekId, w => ({ ...w, ...patch })));
  const deleteWeek = (unitId: string, topicId: string, weekId: string) => updateUnits(mapTopic(unitId, topicId, t => ({ ...t, weeks: t.weeks.filter(w => w.id !== weekId) })));
  const updateWeekTopic = (unitId: string, topicId: string, weekId: string, newTopicId: string) => updateUnits(mapWeek(unitId, topicId, weekId, w => ({ ...w, topicId: newTopicId })));

  // ── Subtopic ops ──────────────────────────────────────────────────────────
  const addSubtopic    = (unitId: string, topicId: string, weekId: string) => updateUnits(mapWeek(unitId, topicId, weekId, w => ({ ...w, subtopics: [...w.subtopics, { id: uid(), name: `Subtopic ${w.subtopics.length + 1}`, marks: 0, prerequisites: [] }] })));
  const updateSubtopic = (unitId: string, topicId: string, weekId: string, subtopicId: string, patch: Partial<Subtopic>) => updateUnits(mapWeek(unitId, topicId, weekId, w => ({ ...w, subtopics: w.subtopics.map(st => st.id !== subtopicId ? st : { ...st, ...patch }) })));
  const deleteSubtopic = (unitId: string, topicId: string, weekId: string, subtopicId: string) => updateUnits(mapWeek(unitId, topicId, weekId, w => ({ ...w, subtopics: w.subtopics.filter(st => st.id !== subtopicId) })));

  // ── Day ops ───────────────────────────────────────────────────────────────
  const addDay = (unitId: string, topicId: string, weekId: string) => {
    updateUnits(mapWeek(unitId, topicId, weekId, w => {
      const daysOfWeek: Day['dayOfWeek'][] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const usedDays = w.days.map(d => d.dayOfWeek);
      const available = daysOfWeek.find(d => !usedDays.includes(d));
      if (available) {
        const isSunday = available === 'Sunday';
        return {
          ...w,
          days: [...w.days, {
            id: uid(), dayOfWeek: available, objectives: [], activities: [], materials: [], homework: '',
            isHoliday: isSunday, holidayType: isSunday ? 'weekly_off' : undefined, holidayReason: isSunday ? 'Weekly Holiday' : '',
            isReviewDay: isSunday || (w.weekNumber !== undefined && w.weekNumber % 2 === 0 && available === 'Saturday'),
            notes: '', customTitle: isSunday ? 'Holiday - Weekly Off' : '', subtopicId: undefined, topicId: w.topicId,
          } as Day],
        };
      } else {
        showSnackBar('All days already added', 'warning');
        return w;
      }
    }));
  };
  const updateDay = (unitId: string, topicId: string, weekId: string, dayId: string, patch: Partial<Day>) => updateUnits(mapWeek(unitId, topicId, weekId, w => ({ ...w, days: w.days.map(d => d.id === dayId ? { ...d, ...patch } : d) })));
  const deleteDay = (unitId: string, topicId: string, weekId: string, dayId: string) => updateUnits(mapWeek(unitId, topicId, weekId, w => ({ ...w, days: w.days.filter(d => d.id !== dayId) })));

  // ── Save ───────────────────────────────────────────────────────────────────
  const saveSyllabusToApi = async (): Promise<string | null> => {
    if (!currentKnowledgeMap || !selectedBoard || !selectedClass || !selectedSubject) return null;

    const allTopics: any[] = [];
    units.forEach(unit => {
      unit.topics.forEach(topic => {
        allTopics.push({
          topic: topic.name, description: topic.description || '', duration: topic.duration || topic.weeks.length,
          marks: topic.marks || 0, topic_prerequisites: topic.prerequisites || [], learning_material: topic.learning_material || [],
          weeks: topic.weeks.map((w) => ({
            week: w.name, weekNumber: w.weekNumber, topicId: w.topicId || topic.id,
            subtopics: w.subtopics.map((st) => ({ name: st.name, marks: st.marks || 0, prerequisites: st.prerequisites || [] })),
            days: w.days.map((d) => ({
              dayOfWeek: d.dayOfWeek, objectives: d.objectives, activities: d.activities, materials: d.materials,
              homework: d.homework || '', isHoliday: d.isHoliday, holidayType: d.holidayType, holidayReason: d.holidayReason || '',
              isReviewDay: d.isReviewDay, notes: d.notes || '', customTitle: d.customTitle || '',
              subtopicId: d.subtopicId || '', topicId: d.topicId || w.topicId || topic.id,
            })),
            notes: w.notes || '',
          })),
        });
      });
    });

    const terms = units.map((unit, index) => ({
      termIndex: index, name: `Term ${index + 1}`,
      units: [{ id: unit.id, name: unit.name, subtopics: unit.topics.flatMap(t => t.weeks.flatMap(w => w.subtopics.map(st => st.name))), marks: unit.topics.reduce((sum, t) => sum + (t.marks || 0), 0), weeksEstimate: unit.topics.reduce((sum, t) => sum + t.duration, 0), description: unit.topics[0]?.description || '' }],
      status: 'draft' as const, coordinatorComment: '',
    }));

    const payload = {
      board: selectedBoard, class: selectedClass, subject: selectedSubject, topics: allTopics, terms,
      knowledgeMapId: currentKnowledgeMap.id ?? currentKnowledgeMap._id, status: 'draft',
    };

    if (syllabusId) { await aiActions.updateSyllabus(syllabusId, payload); return syllabusId; }
    const created = await aiActions.createSyllabus(payload);
    const newId = created?._id ?? created?.data?._id;
    if (newId) setSyllabusId(newId);
    return newId ?? null;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveSyllabusToApi();
      showSnackBar('Syllabus saved successfully!', 'success');
      setHasChanges(false);
    } catch (error) {
      console.error(error);
      showSnackBar('Failed to save syllabus', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!currentKnowledgeMap?.topics) return;
    setUnits(buildDefaultUnits(enrichTopics(currentKnowledgeMap.topics)));
    setHasChanges(false);
    showSnackBar('Reset to original', 'info');
  };

  const handleProceedToCurriculum = async () => {
    if (!selectedBoard || !selectedClass || !selectedSubject) {
      showSnackBar('Please select Board, Class, and Subject first', 'warning');
      return;
    }
    try {
      setSaving(true);
      await saveSyllabusToApi();
      setHasChanges(false);
      onNavigateToCurriculum?.(selectedBoard, selectedClass, selectedSubject);
    } catch (error) {
      console.error(error);
      showSnackBar('Failed to save syllabus before proceeding', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: '#F1F5F9', minHeight: '100vh', paddingLeft: { md: '160px', sm: '100%' } }}>

      {/* Header */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: 'white' }}>
        <DynamicBreadcrumbs items={breadcrumbItems} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {onNavigateToCurriculum && (
            <Button size="medium" variant="contained" startIcon={<ArrowForwardIcon />} onClick={handleProceedToCurriculum}
              sx={{ fontSize: '0.85rem', textTransform: 'none', borderRadius: 2, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, py: 0.75 }}
            >Proceed to Curriculum</Button>
          )}
          <Button size="medium" variant="outlined" startIcon={<TableChartIcon />} onClick={() => setShowTabularView(true)}
            sx={{ fontSize: '0.85rem', textTransform: 'none', borderRadius: 2, py: 0.75 }}
          >Tabular View</Button>
          {hasChanges && (
            <>
              <Button size="medium" variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}
                sx={{ fontSize: '0.85rem', textTransform: 'none', borderRadius: 2, py: 0.75 }}
              >Reset</Button>
              <Button size="medium" variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
                sx={{ fontSize: '0.85rem', textTransform: 'none', borderRadius: 2, py: 0.75 }}
              >{saving ? 'Saving...' : 'Save'}</Button>
            </>
          )}
          {hasChanges && (
            <Chip label="Unsaved Changes" color="warning" size="medium" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 28 }} />
          )}
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2.5, borderRadius: 2, height: 4 }} />}

      {/* Selection Bar */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #E2E8F0', borderRadius: 2, bgcolor: 'white' }}>
        <BoardClassSubjectSelector externalLoading={loading} colProps={{ xs: 12, sm: 3 }} />
      </Paper>

      {/* Empty states */}
      {!loading && (!selectedBoard || !selectedClass || !selectedSubject) && (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2, fontSize: '0.9rem', py: 1.5 }}>
          Select Board, Class, and Subject to load the knowledge map.
        </Alert>
      )}
      {!loading && selectedBoard && selectedClass && selectedSubject && !currentKnowledgeMap && (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2, fontSize: '0.9rem', py: 1.5 }}>
          No knowledge map found for {selectedBoard} — Class {selectedClass} — {selectedSubject}.
        </Alert>
      )}

      {/* Stats */}
      {units.length > 0 && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap', border: '1px solid #E2E8F0', borderRadius: 2, bgcolor: 'white' }}>
          {[
            { label: 'Units',    value: units.length,  color: '#3B82F6' },
            { label: 'Topics',   value: totalTopics,   color: '#8B5CF6' },
            { label: 'Weeks',    value: totalWeeks,    color: '#22C55E' },
            { label: 'Days',     value: totalDays,     color: '#8B5CF6' },
            { label: 'Holidays', value: totalHolidays, color: '#EF4444' },
            { label: 'Marks',    value: totalMarks,    color: '#F59E0B' },
          ].map(s => (
            <Box key={s.label} sx={{ px: 2.5, py: 1, borderRadius: 1.5, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#FAFBFC' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: s.color, fontSize: '1.2rem' }}>{s.value}</Typography>
              <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.8rem' }}>{s.label}</Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Add Unit */}
      {units.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Typography variant="h6" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.95rem' }}>
            {units.length} Unit{units.length !== 1 ? 's' : ''}
          </Typography>
          <Button size="medium" variant="outlined" startIcon={<AddIcon />} onClick={addUnit}
            sx={{ fontSize: '0.8rem', textTransform: 'none', py: 0.5, minHeight: 36, borderRadius: 2, borderColor: '#CBD5E1', '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' } }}
          >Add Unit</Button>
        </Box>
      )}

      {/* Units */}
      {units.map((unit, ui) => (
        <UnitItem
          key={unit.id}
          unit={unit}
          unitIndex={ui}
          onRenameUnit={renameUnit}
          onDeleteUnit={deleteUnit}
          onAddTopic={addTopic}
          onUpdateTopic={updateTopic}
          onDeleteTopic={deleteTopic}
          onAddWeek={addWeek}
          onUpdateWeek={updateWeek}
          onDeleteWeek={deleteWeek}
          onUpdateWeekTopic={updateWeekTopic}
          onAddSubtopic={addSubtopic}
          onUpdateSubtopic={updateSubtopic}
          onDeleteSubtopic={deleteSubtopic}
          onAddDay={addDay}
          onUpdateDay={updateDay}
          onDeleteDay={deleteDay}
        />
      ))}

      {/* Tabular View Dialog */}
      <Dialog open={showTabularView} onClose={() => setShowTabularView(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}>
        <DialogContent sx={{ p: 0 }}>
          <TabularDayPlanner
            units={units}
            onSave={(updatedUnits) => {
              setUnits(updatedUnits);
              setHasChanges(true);
              setShowTabularView(false);
              showSnackBar('Day plan updated successfully!', 'success');
            }}
            onClose={() => setShowTabularView(false)}
            loading={loading}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}